// Globals
import debounce from 'lodash.debounce';
import { ChangeEvent, memo, ReactNode, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

// Store
import { useAppDispatch, useAppState } from '@/store';
import { getOrders, selectAppliedFilters, applyFilters, resetState } from '../../feature';

// Components
import { StyledFilterRow, StyledInput, StyledSelect } from './OrdersFiltersContainerStyles';

// Helpers
import { GetOrdersDTO } from '../../dtos';
import { getPaymentStatusOptions, getStatusOptions, getStepTypeOptions, OrderFiltersFields } from '../../helpers';

type IAdminFiltersContainerProps = {
  children?: ReactNode;
  className?: string;
};

const OrdersFiltersContainer = ({ children, className }: IAdminFiltersContainerProps) => {
  const { t } = useTranslation(['order', 'common']);
  const dispatch = useAppDispatch();
  const filters = useAppState(selectAppliedFilters);

  const handleDebounceSearch = useCallback(
    debounce(async (searchParams) => {
      const dto = new GetOrdersDTO(searchParams);
      dispatch(getOrders(dto));
    }, 500),
    [dispatch],
  );

  const handleSearch = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      const newQuery = { ...filters, [name]: value || undefined };

      dispatch(applyFilters(newQuery));
      handleDebounceSearch(newQuery);
    },
    [filters],
  );

  const handleSelect = useCallback(
    (name: string) => (value: unknown) => {
      const newQuery = {
        ...filters,
        [name]: value,
      };
      dispatch(applyFilters(newQuery));
      handleDebounceSearch(newQuery);
    },
    [filters],
  );

  useEffect(() => {
    return () => {
      dispatch(resetState());
      handleDebounceSearch.cancel();
    };
  }, []);

  return (
    <thead className={className}>
      {children}
      <StyledFilterRow>
        <th>
          <StyledInput
            onChange={handleSearch}
            name={OrderFiltersFields.TRIPLETEX_ORDER_NUMBER}
            placeholder={t('common_search', { ns: 'common' })}
            allowClear
            size="middle"
          />
        </th>
        <th>
          <StyledSelect
            onChange={handleSelect(OrderFiltersFields.STEP_NAME)}
            options={getStepTypeOptions(t)}
            suffixIcon={null}
            size="middle"
            placeholder={t('order_type')}
            allowClear
          />
        </th>
        <th>
          <StyledInput
            onChange={handleSearch}
            placeholder={t('common_search', { ns: 'common' })}
            name={OrderFiltersFields.ADDRESS}
            size="middle"
            allowClear
          />
        </th>
        <th />
        <th>
          <StyledInput
            onChange={handleSearch}
            name={OrderFiltersFields.USER}
            placeholder={t('common_search', { ns: 'common' })}
            allowClear
            size="middle"
          />
        </th>
        <th />
        <th>
          <StyledSelect
            onChange={handleSelect(OrderFiltersFields.PAYMENT_STATUS)}
            options={getPaymentStatusOptions(t)}
            placeholder={t('payment_status')}
            suffixIcon={null}
            allowClear
            size="middle"
          />
        </th>
        <th>
          <StyledSelect
            onChange={handleSelect(OrderFiltersFields.STATUS)}
            options={getStatusOptions(t)}
            placeholder={t('status')}
            suffixIcon={null}
            allowClear
            size="middle"
          />
        </th>
      </StyledFilterRow>
    </thead>
  );
};

export default memo(OrdersFiltersContainer);
